import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import * as adminService from '../../services/admin';

const BackupManagement = () => {
    const [activeTab, setActiveTab] = useState('backups');
    const [backups, setBackups] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showBackupModal, setShowBackupModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const [backupForm, setBackupForm] = useState({
        backup_name: '',
        backup_type: 'full',
        tables: []
    });

    const [scheduleForm, setScheduleForm] = useState({
        id: null,
        schedule_name: '',
        frequency: 'daily',
        scheduled_time: '02:00',
        day_of_week: 0,
        day_of_month: 1,
        backup_type: 'full',
        tables_to_backup: [],
        retention_days: 30,
        is_active: true
    });

    useEffect(() => {
        loadBackups();
        loadSchedules();
        loadTables();
    }, []);

    const loadBackups = async () => {
        try {
            setLoading(true);
            const response = await adminService.listBackups({});
            if (response.status === 'success') {
                setBackups(response.data);
            }
        } catch (error) {
            console.error('Error loading backups:', error);
            toast.error('Failed to load backups');
        } finally {
            setLoading(false);
        }
    };

    const loadSchedules = async () => {
        try {
            const response = await adminService.listBackupSchedules();
            if (response.status === 'success') {
                setSchedules(response.data);
            }
        } catch (error) {
            console.error('Error loading schedules:', error);
            toast.error('Failed to load schedules');
        }
    };

    const loadTables = async () => {
        try {
            const response = await adminService.getDatabaseTables();
            if (response.status === 'success') {
                setTables(response.data);
            }
        } catch (error) {
            console.error('Error loading tables:', error);
        }
    };

    const handleCreateBackup = async () => {
        if (!backupForm.backup_name) {
            toast.error('Please enter backup name');
            return;
        }

        if (backupForm.backup_type === 'partial' && backupForm.tables.length === 0) {
            toast.error('Please select tables for partial backup');
            return;
        }

        try {
            setLoading(true);
            const response = await adminService.createBackup(backupForm);
            if (response.status === 'success') {
                toast.success('Backup created successfully');
                setShowBackupModal(false);
                loadBackups();
                setBackupForm({
                    backup_name: '',
                    backup_type: 'full',
                    tables: []
                });
            } else {
                toast.error(response.message || 'Failed to create backup');
            }
        } catch (error) {
            console.error('Error creating backup:', error);
            toast.error('Failed to create backup');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadBackup = async (id, fileName) => {
        try {
            const response = await adminService.downloadBackup(id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Backup downloaded successfully');
        } catch (error) {
            console.error('Error downloading backup:', error);
            toast.error('Failed to download backup');
        }
    };

    const handleRestoreBackup = async (id, backupName) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            html: `<strong>WARNING:</strong> This will restore the database from backup <strong>${backupName}</strong>.<br><br>All current data will be replaced!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, restore it!',
            input: 'text',
            inputPlaceholder: 'Type RESTORE to confirm',
            inputValidator: (value) => {
                if (value !== 'RESTORE') {
                    return 'You must type RESTORE to confirm'
                }
            }
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminService.restoreBackup(id);
                if (response.status === 'success') {
                    toast.success('Database restored successfully');
                } else {
                    toast.error(response.message || 'Failed to restore backup');
                }
            } catch (error) {
                console.error('Error restoring backup:', error);
                toast.error('Failed to restore backup');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteBackup = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This backup will be deleted permanently',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await adminService.deleteBackup(id);
                if (response.status === 'success') {
                    toast.success('Backup deleted successfully');
                    loadBackups();
                } else {
                    toast.error(response.message || 'Failed to delete backup');
                }
            } catch (error) {
                console.error('Error deleting backup:', error);
                toast.error('Failed to delete backup');
            }
        }
    };

    const handleSaveSchedule = async () => {
        if (!scheduleForm.schedule_name) {
            toast.error('Please enter schedule name');
            return;
        }

        try {
            setLoading(true);
            let response;
            if (editMode) {
                response = await adminService.updateBackupSchedule(scheduleForm.id, scheduleForm);
            } else {
                response = await adminService.createBackupSchedule(scheduleForm);
            }

            if (response.status === 'success') {
                toast.success(`Schedule ${editMode ? 'updated' : 'created'} successfully`);
                setShowScheduleModal(false);
                loadSchedules();
            } else {
                toast.error(response.message || 'Failed to save schedule');
            }
        } catch (error) {
            console.error('Error saving schedule:', error);
            toast.error('Failed to save schedule');
        } finally {
            setLoading(false);
        }
    };

    const handleEditSchedule = (schedule) => {
        setScheduleForm({
            id: schedule.id,
            schedule_name: schedule.schedule_name,
            frequency: schedule.frequency,
            scheduled_time: schedule.scheduled_time.substring(0, 5),
            day_of_week: schedule.day_of_week || 0,
            day_of_month: schedule.day_of_month || 1,
            backup_type: schedule.backup_type,
            tables_to_backup: schedule.tables_to_backup ? JSON.parse(schedule.tables_to_backup) : [],
            retention_days: schedule.retention_days,
            is_active: schedule.is_active
        });
        setEditMode(true);
        setShowScheduleModal(true);
    };

    const handleDeleteSchedule = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This schedule will be deleted',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await adminService.deleteBackupSchedule(id);
                if (response.status === 'success') {
                    toast.success('Schedule deleted successfully');
                    loadSchedules();
                } else {
                    toast.error(response.message || 'Failed to delete schedule');
                }
            } catch (error) {
                console.error('Error deleting schedule:', error);
                toast.error('Failed to delete schedule');
            }
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'in_progress': 'bg-warning',
            'completed': 'bg-success',
            'failed': 'bg-danger'
        };
        return badges[status] || 'bg-secondary';
    };

    const backupColumns = [
        {
            headerName: 'Backup Name',
            field: 'backup_name',
            sortable: true,
            filter: true,
            width: 200
        },
        {
            headerName: 'Type',
            field: 'backup_type',
            sortable: true,
            filter: true,
            width: 110,
            cellRenderer: (params) => (
                <span className="badge bg-info text-capitalize">{params.value}</span>
            )
        },
        {
            headerName: 'File Name',
            field: 'file_name',
            sortable: true,
            filter: true,
            width: 200
        },
        {
            headerName: 'Size',
            field: 'file_size_human',
            sortable: true,
            width: 100
        },
        {
            headerName: 'Status',
            field: 'status',
            sortable: true,
            filter: true,
            width: 120,
            cellRenderer: (params) => (
                <span className={`badge ${getStatusBadge(params.value)} text-capitalize`}>
                    {params.value.replace('_', ' ')}
                </span>
            )
        },
        {
            headerName: 'Created By',
            field: 'created_by',
            sortable: true,
            filter: true,
            width: 130
        },
        {
            headerName: 'Started At',
            field: 'started_at',
            sortable: true,
            filter: true,
            width: 160,
            cellRenderer: (params) => params.value ? moment(params.value).format('DD-MMM-YYYY HH:mm') : '-'
        },
        {
            headerName: 'Actions',
            field: 'id',
            width: 200,
            cellRenderer: (params) => (
                <div>
                    {params.data.status === 'completed' && (
                        <>
                            <button
                                className="btn btn-sm btn-success me-1"
                                onClick={() => handleDownloadBackup(params.value, params.data.file_name)}
                                title="Download"
                            >
                                <i className="fa fa-download"></i>
                            </button>
                            <button
                                className="btn btn-sm btn-warning me-1"
                                onClick={() => handleRestoreBackup(params.value, params.data.backup_name)}
                                title="Restore"
                            >
                                <i className="fa fa-undo"></i>
                            </button>
                        </>
                    )}
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteBackup(params.value)}
                        title="Delete"
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="container-fluid mt-3">
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fa fa-database"></i> Data Backup & Recovery
                    </h5>
                </div>
                <div className="card-body">
                    {/* Tabs */}
                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'backups' ? 'active' : ''}`}
                                onClick={() => setActiveTab('backups')}
                            >
                                <i className="fa fa-archive me-2"></i>
                                Backups
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'schedules' ? 'active' : ''}`}
                                onClick={() => setActiveTab('schedules')}
                            >
                                <i className="fa fa-clock me-2"></i>
                                Schedules
                            </button>
                        </li>
                    </ul>

                    {/* Backups Tab */}
                    {activeTab === 'backups' && (
                        <div>
                            <div className="mb-3">
                                <button
                                    className="btn btn-success"
                                    onClick={() => {
                                        setBackupForm({
                                            backup_name: '',
                                            backup_type: 'full',
                                            tables: []
                                        });
                                        setShowBackupModal(true);
                                    }}
                                >
                                    <i className="fa fa-plus"></i> Create Backup
                                </button>
                            </div>

                            <div className="ag-theme-alpine" style={{ height: '500px', width: '100%' }}>
                                <AgGridReact
                                    rowData={backups}
                                    columnDefs={backupColumns}
                                    pagination={true}
                                    paginationPageSize={20}
                                    domLayout='autoHeight'
                                />
                            </div>
                        </div>
                    )}

                    {/* Schedules Tab */}
                    {activeTab === 'schedules' && (
                        <div>
                            <div className="mb-3">
                                <button
                                    className="btn btn-success"
                                    onClick={() => {
                                        setScheduleForm({
                                            id: null,
                                            schedule_name: '',
                                            frequency: 'daily',
                                            scheduled_time: '02:00',
                                            day_of_week: 0,
                                            day_of_month: 1,
                                            backup_type: 'full',
                                            tables_to_backup: [],
                                            retention_days: 30,
                                            is_active: true
                                        });
                                        setEditMode(false);
                                        setShowScheduleModal(true);
                                    }}
                                >
                                    <i className="fa fa-plus"></i> New Schedule
                                </button>
                            </div>

                            {schedules.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Schedule Name</th>
                                                <th>Frequency</th>
                                                <th>Time</th>
                                                <th>Type</th>
                                                <th>Retention</th>
                                                <th>Last Run</th>
                                                <th>Next Run</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {schedules.map(schedule => (
                                                <tr key={schedule.id}>
                                                    <td>{schedule.schedule_name}</td>
                                                    <td className="text-capitalize">{schedule.frequency}</td>
                                                    <td>{schedule.scheduled_time}</td>
                                                    <td>
                                                        <span className="badge bg-info text-capitalize">
                                                            {schedule.backup_type}
                                                        </span>
                                                    </td>
                                                    <td>{schedule.retention_days} days</td>
                                                    <td>
                                                        {schedule.last_run_at
                                                            ? moment(schedule.last_run_at).format('DD-MMM-YYYY HH:mm')
                                                            : 'Never'}
                                                    </td>
                                                    <td>
                                                        {schedule.next_run_at
                                                            ? moment(schedule.next_run_at).format('DD-MMM-YYYY HH:mm')
                                                            : '-'}
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${schedule.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                            {schedule.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-primary me-2"
                                                            onClick={() => handleEditSchedule(schedule)}
                                                        >
                                                            <i className="fa fa-edit"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDeleteSchedule(schedule.id)}
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center text-muted py-5">
                                    <i className="fa fa-clock fa-3x mb-3"></i>
                                    <p>No backup schedules configured</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Backup Modal */}
            {showBackupModal && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Create Backup</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowBackupModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Backup Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={backupForm.backup_name}
                                        onChange={(e) => setBackupForm({ ...backupForm, backup_name: e.target.value })}
                                        placeholder="e.g., Monthly Backup - January 2024"
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Backup Type *</label>
                                    <select
                                                className="default-select  form-control wide"
                                        value={backupForm.backup_type}
                                        onChange={(e) => setBackupForm({ ...backupForm, backup_type: e.target.value })}
                                    >
                                        <option value="full">Full Backup (All Tables)</option>
                                        <option value="partial">Partial Backup (Selected Tables)</option>
                                    </select>
                                </div>

                                {backupForm.backup_type === 'partial' && (
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Select Tables *</label>
                                        <div style={{maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px'}}>
                                            {tables.map(table => (
                                                <div key={table} className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`table-${table}`}
                                                        checked={backupForm.tables.includes(table)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setBackupForm({
                                                                    ...backupForm,
                                                                    tables: [...backupForm.tables, table]
                                                                });
                                                            } else {
                                                                setBackupForm({
                                                                    ...backupForm,
                                                                    tables: backupForm.tables.filter(t => t !== table)
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor={`table-${table}`}>
                                                        {table}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowBackupModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleCreateBackup}
                                    disabled={loading}
                                >
                                    <i className="fa fa-save"></i> Create Backup
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Schedule Modal */}
            {showScheduleModal && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editMode ? 'Edit Schedule' : 'New Schedule'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowScheduleModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Schedule Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={scheduleForm.schedule_name}
                                            onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_name: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Frequency *</label>
                                        <select
                                            className="form-select"
                                            value={scheduleForm.frequency}
                                            onChange={(e) => setScheduleForm({ ...scheduleForm, frequency: e.target.value })}
                                        >
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Time *</label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={scheduleForm.scheduled_time}
                                            onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_time: e.target.value })}
                                        />
                                    </div>

                                    {scheduleForm.frequency === 'weekly' && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">Day of Week *</label>
                                            <select
                                                className="default-select  form-control wide"
                                                value={scheduleForm.day_of_week}
                                                onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_week: parseInt(e.target.value) })}
                                            >
                                                <option value="0">Sunday</option>
                                                <option value="1">Monday</option>
                                                <option value="2">Tuesday</option>
                                                <option value="3">Wednesday</option>
                                                <option value="4">Thursday</option>
                                                <option value="5">Friday</option>
                                                <option value="6">Saturday</option>
                                            </select>
                                        </div>
                                    )}

                                    {scheduleForm.frequency === 'monthly' && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">Day of Month *</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min="1"
                                                max="31"
                                                value={scheduleForm.day_of_month}
                                                onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_month: parseInt(e.target.value) })}
                                            />
                                        </div>
                                    )}

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Retention (Days) *</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            min="1"
                                            value={scheduleForm.retention_days}
                                            onChange={(e) => setScheduleForm({ ...scheduleForm, retention_days: parseInt(e.target.value) })}
                                        />
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Backup Type *</label>
                                        <select
                                            className="form-select"
                                            value={scheduleForm.backup_type}
                                            onChange={(e) => setScheduleForm({ ...scheduleForm, backup_type: e.target.value })}
                                        >
                                            <option value="full">Full Backup</option>
                                            <option value="partial">Partial Backup</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Status</label>
                                        <div className="form-check form-switch mt-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={scheduleForm.is_active}
                                                onChange={(e) => setScheduleForm({ ...scheduleForm, is_active: e.target.checked })}
                                            />
                                            <label className="form-check-label">
                                                {scheduleForm.is_active ? 'Active' : 'Inactive'}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowScheduleModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveSchedule}
                                    disabled={loading}
                                >
                                    <i className="fa fa-save"></i> Save Schedule
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BackupManagement;
